import type { ColDef } from "ag-grid-community"

function sameKey(a: readonly unknown[], b: readonly unknown[]): boolean {
  if (a.length !== b.length) return false
  return a.every((value, index) => value === b[index])
}

/**
 * Reuses a column definition object whenever the inputs that shape it are unchanged.
 *
 * A freshly built definition counts as a changed column and is refreshed, since colDef
 * functions compare by reference. The key must name every value the definition reads — one
 * missing from the key is a stale column.
 */
export function createColDefCache<Row>() {
  const entries = new Map<string, { key: readonly unknown[]; colDef: ColDef<Row> }>()

  return function reuseColDef(
    colId: string,
    key: readonly unknown[],
    build: () => ColDef<Row>,
  ): ColDef<Row> {
    const cached = entries.get(colId)
    if (cached && sameKey(cached.key, key)) return cached.colDef

    const colDef = build()
    entries.set(colId, { key, colDef })
    return colDef
  }
}

/**
 * Keeps the previous array when it holds the same definitions in the same order, so ag-grid
 * does no column work and the effects watching the definitions never fire. Element identity is
 * the whole comparison, so this only pays off alongside {@link createColDefCache}: a single
 * freshly built definition is enough to miss.
 */
export function createColDefArrayCache<Row>() {
  let previous: ColDef<Row>[] | null = null

  return function reuseColDefs(next: ColDef<Row>[]): ColDef<Row>[] {
    if (
      previous &&
      previous.length === next.length &&
      previous.every((colDef, index) => colDef === next[index])
    ) {
      return previous
    }
    previous = next
    return next
  }
}
